import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  DoubleMaterialityConsolidatedResultRowDto,
} from "@green0meter/api-client";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityResultsApi } from "@api/services";
import { TANSTACK_QUERY_STALE_TIME } from "@constants";

export const ACTION_GET_CONSOLIDATED_RESULTS = "GET_CONSOLIDATED_RESULTS";

export type DoubleMaterialityConsolidatedResultRow =
  DoubleMaterialityConsolidatedResultRowDto;

type FetchParams = {
  year: number;
  impactThreshold: number;
  financialThreshold: number;
};

const fetchConsolidatedResults = async (
  params: FetchParams,
): Promise<DoubleMaterialityConsolidatedResultRowDto[]> => {
  const response =
    await doubleMaterialityResultsApi.doubleMaterialityResultsControllerGetConsolidatedResults(
      params.year,
      params.impactThreshold,
      params.financialThreshold,
    );

  return response.data ?? [];
};

export const useGetConsolidatedResults = (
  user: UserProfileDto | null,
  params: FetchParams,
  options?: { enabled?: boolean },
): {
  consolidatedResults?: DoubleMaterialityConsolidatedResultRowDto[];
  isPendingConsolidatedResults: boolean;
  refetchConsolidatedResults: () => void;
} => {
  const canAccess = roleGuard(user, [UserRole.Owner, UserRole.Manager]);
  const isEnabled =
    canAccess && (options?.enabled ?? true) && Number.isFinite(params.year);

  const { data, isPending, refetch } = useQuery({
    queryKey: [
      DOMAIN_DOUBLE_MATERIALITY,
      ACTION_GET_CONSOLIDATED_RESULTS,
      params.year,
      params.impactThreshold,
      params.financialThreshold,
    ],
    queryFn: () => fetchConsolidatedResults(params),
    enabled: isEnabled,
    staleTime: TANSTACK_QUERY_STALE_TIME,
    placeholderData: keepPreviousData,
  });

  return {
    consolidatedResults: data,
    isPendingConsolidatedResults: isPending,
    refetchConsolidatedResults: refetch,
  };
};
